// ✅ src/components/Hero.js (PRO - UPDATED)
// ✅ Keeps your props: onBuyClick, onSellClick, onAboutClick, onEarnClick
// ✅ Cleaner hero: 1 primary CTA + 2 text links
// ✅ Adds trust strip for Ghana (MoMo/Bank/No commission/Cancel anytime)
// ✅ Fix: FAQs scroll target is "faqs" (not how-it-works)

import React from "react";
import "../App.css";
import { FiShoppingBag, FiArrowRight, FiCheckCircle } from "react-icons/fi";

export default function Hero({
  onBuyClick,
  onSellClick,
  onAboutClick,
  onEarnClick,
}) {
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="lp">
      {/* NAV */}
      <header className="lp-nav">
        <button
          type="button"
          className="lp-brand"
          onClick={() => scrollToId("top")}
          aria-label="Go to top"
        >
          <div className="lp-logo" aria-hidden="true">
            <FiShoppingBag />
          </div>
          <div className="lp-brand-name">YourStore</div>
        </button>

        {/* Links hide on mobile */}
        <nav className="lp-links" aria-label="Primary">
          <button
            type="button"
            className="lp-link"
            onClick={() => scrollToId("features")}
          >
            Features
          </button>

          <button
            type="button"
            className="lp-link"
            onClick={() => scrollToId("how-it-works")}
          >
            How it works
          </button>

          <button
            type="button"
            className="lp-link"
            onClick={() => scrollToId("pricing")}
          >
            Pricing
          </button>

          <button
            type="button"
            className="lp-link"
            onClick={() => scrollToId("faqs")}
          >
            FAQs
          </button>
        </nav>

        {/* Actions always visible */}
        <div className="lp-actions">
          <button type="button" className="lp-login" onClick={onEarnClick}>
            Earn Money
          </button>

          <button type="button" className="lp-login" onClick={onSellClick}>
            Login
          </button>

          <button type="button" className="lp-cta" onClick={onBuyClick}>
            Start Free Trial
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="lp-hero" id="top">
        <div className="lp-hero-inner">
          <div>
            <h1 className="lp-title">
              Create Your Own <br />
              <span className="lp-title-accent">Online Store</span>
            </h1>

            <p className="lp-subtitle">
              Launch your shop in minutes, share your link on WhatsApp & TikTok,
              and get paid directly.{" "}
              <b>₵60/month</b> — no commissions, keep 100% of your sales.
            </p>

            {/* Primary CTA + soft links */}
            <div className="lp-btns">
              <button
                type="button"
                className="lp-btn-primary"
                onClick={onBuyClick}
              >
                Start Free Trial <FiArrowRight style={{ marginLeft: 8 }} />
              </button>

              <button
                type="button"
                className="lp-btn-secondary"
                onClick={() => scrollToId("how-it-works")}
              >
                See how it works
              </button>

              <button
                type="button"
                className="lp-btn-secondary"
                onClick={onAboutClick}
              >
                What’s this about?
              </button>
            </div>

            {/* Trust strip */}
            <div className="lp-trust" style={{ marginTop: 14 }}>
              <div className="lp-trust-item">
                <FiCheckCircle aria-hidden="true" /> MoMo & Bank transfers
              </div>
              <div className="lp-trust-item">
                <FiCheckCircle aria-hidden="true" /> No commissions
              </div>
              <div className="lp-trust-item">
                <FiCheckCircle aria-hidden="true" /> Cancel anytime
              </div>
              <div className="lp-trust-item">
                <FiCheckCircle aria-hidden="true" /> Works on mobile
              </div>
            </div>
          </div>

          <div className="lp-illustration">
            <img
              src="/illustrations/ecommerce-dashboard.png"
              alt="YourStore dashboard preview"
              className="lp-hero-illustration"
              loading="lazy"
            />
          </div>
        </div>

        {/* FEATURES */}
        <div className="lp-features" id="features">
          <div className="lp-card">
            <img
              src="/illustrations/store-link.png"
              className="lp-feature-img"
              alt="Store link"
              loading="lazy"
            />
            <h3>Your Own Store Link</h3>
            <p>Get a clean shop link you can share anywhere.</p>
          </div>

          <div className="lp-card">
            <img
              src="/illustrations/payments.png"
              className="lp-feature-img"
              alt="Payments"
              loading="lazy"
            />
            <h3>Easy Payments</h3>
            <p>Accept MoMo & bank transfers directly from customers.</p>
          </div>

          <div className="lp-card">
            <img
              src="/illustrations/orders.png"
              className="lp-feature-img"
              alt="Orders"
              loading="lazy"
            />
            <h3>Manage Orders</h3>
            <p>Track, confirm, and fulfill customer orders easily.</p>
          </div>

          <div className="lp-card">
            <img
              src="/illustrations/delivery.png"
              className="lp-feature-img"
              alt="Delivery"
              loading="lazy"
            />
            <h3>Deliver Your Way</h3>
            <p>Handle delivery yourself or with your preferred rider.</p>
          </div>
        </div>

        {/* HOW IT WORKS */}
        <div className="lp-how" id="how-it-works">
          <h2>How It Works</h2>

          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-num">1</div>
              <div>
                <h4>Create Your Store</h4>
                <p>Add products, price, and payment details.</p>
              </div>
            </div>

            <div className="lp-step">
              <div className="lp-num">2</div>
              <div>
                <h4>Share Your Link</h4>
                <p>Post it on WhatsApp Status, TikTok, Instagram, anywhere.</p>
              </div>
            </div>

            <div className="lp-step">
              <div className="lp-num">3</div>
              <div>
                <h4>Get Paid & Deliver</h4>
                <p>Receive payment directly and deliver to your customer.</p>
              </div>
            </div>
          </div>
        </div>

        {/* PRICING CTA */}
        <div className="lp-bottom" id="pricing">
          <h2>Start Your Online Store Today</h2>
          <p>
            Only <b>₵60</b>/month — no commissions. You keep <b>100%</b> of your
            sales.
          </p>

          <button type="button" className="lp-btn-primary" onClick={onBuyClick}>
            Start Free Trial <FiArrowRight style={{ marginLeft: 8 }} />
          </button>

          <div style={{ marginTop: 10, opacity: 0.8 }}>
            <small>Cancel anytime • Works on mobile • Shareable store link</small>
          </div>
        </div>

        {/* FAQ ANCHOR (so nav works) */}
        <div id="faqs" style={{ height: 1 }} />
      </section>
    </div>
  );
}
